import type {
    PageLayoutOptions,
    Revision,
    RevisionConflicts,
    RevisionFile,
    RevisionPage,
    RevisionPageDocumentCover,
    RevisionReusableContent,
    RevisionTags,
    RevisionVariables,
} from '../types';

/**
 * Cover properties compared by value. Content references are compared separately by
 * {@link refAreEqual}. Typed as an exhaustive record so a new cover property fails to compile
 * until it is accounted for here.
 */
const PAGE_COVER_VALUE_KEYS = {
    textColor: true,
    textColorDark: true,
    yPos: true,
    height: true,
} satisfies Record<Exclude<keyof RevisionPageDocumentCover, 'ref' | 'refDark'>, true>;

/**
 * Layout options compared by {@link layoutAreEqual}. Typed as an exhaustive record so a new
 * layout option fails to compile until it is accounted for here.
 */
const PAGE_LAYOUT_OPTION_KEYS = {
    cover: true,
    coverMask: true,
    coverSize: true,
    width: true,
    title: true,
    description: true,
    tableOfContents: true,
    outline: true,
    pagination: true,
    metadata: true,
    tags: true,
    actions: true,
    anchors: true,
} satisfies Record<keyof PageLayoutOptions, true>;

/**
 * Returns true if two revisions are equal (same id and same metadata).
 */
export function revisionsAreEqual(lhs: Revision, rhs: Revision): boolean {
    // Check RevisionInfo properties
    if (lhs.id !== rhs.id) {
        return false;
    }

    if (lhs.type !== rhs.type) {
        return false;
    }

    if (lhs.contentId !== rhs.contentId) {
        return false;
    }

    // Check edits
    if (!editsAreEqual(lhs.edits, rhs.edits)) {
        return false;
    }

    // Check parents array
    if (!arraysAreEqual(lhs.parents, rhs.parents)) {
        return false;
    }

    // Check optional properties
    if (lhs.mergedFrom !== rhs.mergedFrom) {
        return false;
    }

    if (lhs.rolledBackBy !== rhs.rolledBackBy) {
        return false;
    }

    if (lhs.schemaVersion !== rhs.schemaVersion) {
        return false;
    }

    // Check git metadata
    if (!gitStateAreEqual(lhs.git, rhs.git)) {
        return false;
    }

    // Check Revision-specific properties

    // Check rootPages array
    if (!arraysAreEqual(lhs.rootPages, rhs.rootPages)) {
        return false;
    }

    // Check files
    if (!filesAreEqual(lhs.files, rhs.files)) {
        return false;
    }

    // Check pages
    if (!pagesAreEqual(lhs.pages, rhs.pages)) {
        return false;
    }

    // Check reusableContents
    if (!reusableContentsAreEqual(lhs.reusableContents, rhs.reusableContents)) {
        return false;
    }

    // Check variables
    if (!variablesAreEqual(lhs.variables, rhs.variables)) {
        return false;
    }

    // Check tags
    if (!tagsAreEqual(lhs.tags, rhs.tags)) {
        return false;
    }

    // Check conflicts
    if (!conflictsAreEqual(lhs.conflicts, rhs.conflicts)) {
        return false;
    }

    return true;
}

function editsAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs || !rhs) return false;

    // Edits is a map of user IDs to edit info
    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (!(key in rhs)) return false;

        const lhsEdit = lhs[key];
        const rhsEdit = rhs[key];

        if (lhsEdit.updatedAt !== rhsEdit.updatedAt || lhsEdit.count !== rhsEdit.count) {
            return false;
        }
    }

    return true;
}

function arraysAreEqual(lhs: string[], rhs: string[]): boolean {
    if (lhs === rhs) return true;
    if (lhs.length !== rhs.length) return false;

    for (let i = 0; i < lhs.length; i++) {
        if (lhs[i] !== rhs[i]) return false;
    }

    return true;
}

function gitStateAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    // Check commit state
    if (!lhs.commit && !rhs.commit) return true;
    if (!lhs.commit || !rhs.commit) return false;

    // Compare GitCommitState properties
    return (
        lhs.commit.oid === rhs.commit.oid &&
        lhs.commit.message === rhs.commit.message &&
        lhs.commit.createdByGitBook === rhs.commit.createdByGitBook &&
        lhs.commit.url === rhs.commit.url &&
        lhs.commit.ref === rhs.commit.ref
    );
}

function filesAreEqual(
    lhs: { [id: string]: RevisionFile },
    rhs: { [id: string]: RevisionFile }
): boolean {
    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (!(key in rhs)) return false;

        const lhsFile = lhs[key];
        const rhsFile = rhs[key];

        if (
            lhsFile.id !== rhsFile.id ||
            lhsFile.name !== rhsFile.name ||
            lhsFile.size !== rhsFile.size ||
            lhsFile.contentType !== rhsFile.contentType ||
            lhsFile.downloadURL !== rhsFile.downloadURL ||
            lhsFile.blobId !== rhsFile.blobId ||
            !editsAreEqual(lhsFile.edits, rhsFile.edits) ||
            !dimensionsAreEqual(lhsFile.dimensions, rhsFile.dimensions) ||
            !gitFileStateAreEqual(lhsFile.git, rhsFile.git)
        ) {
            return false;
        }
    }

    return true;
}

function dimensionsAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    return lhs.height === rhs.height && lhs.width === rhs.width;
}

function gitFileStateAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    return lhs.oid === rhs.oid && lhs.filePath === rhs.filePath;
}

function pagesAreEqual(
    lhs: { [key: string]: RevisionPage },
    rhs: { [key: string]: RevisionPage }
): boolean {
    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (!(key in rhs)) return false;

        const lhsPage = lhs[key];
        const rhsPage = rhs[key];

        if (!pageAreEqual(lhsPage, rhsPage)) return false;
    }

    return true;
}

function pageAreEqual(lhs: RevisionPage, rhs: RevisionPage): boolean {
    // Check base properties
    if (
        lhs.id !== rhs.id ||
        lhs.type !== rhs.type ||
        lhs.title !== rhs.title ||
        lhs.linkTitle !== rhs.linkTitle ||
        lhs.emoji !== rhs.emoji ||
        lhs.icon !== rhs.icon ||
        lhs.condition !== rhs.condition ||
        !editsAreEqual(lhs.edits, rhs.edits) ||
        !gitFileStateAreEqual(lhs.git, rhs.git)
    ) {
        return false;
    }

    // Check type-specific properties
    if (lhs.type === 'document' && rhs.type === 'document') {
        return (
            lhs.slug === rhs.slug &&
            lhs.description === rhs.description &&
            lhs.tagTitle === rhs.tagTitle &&
            lhs.document === rhs.document &&
            lhs.hidden === rhs.hidden &&
            lhs.noIndex === rhs.noIndex &&
            lhs.noRobotsIndex === rhs.noRobotsIndex &&
            arraysAreEqual(lhs.pages, rhs.pages) &&
            coverAreEqual(lhs.cover, rhs.cover) &&
            layoutAreEqual(lhs.layout, rhs.layout) &&
            computedSourceAreEqual(lhs.computed, rhs.computed) &&
            variablesAreEqual(lhs.variables, rhs.variables) &&
            deepEqual(lhs.metaLinks, rhs.metaLinks) &&
            deepEqual(lhs.tags, rhs.tags)
        );
    }

    if (lhs.type === 'group' && rhs.type === 'group') {
        return (
            lhs.slug === rhs.slug &&
            lhs.hidden === rhs.hidden &&
            lhs.noIndex === rhs.noIndex &&
            lhs.noRobotsIndex === rhs.noRobotsIndex &&
            arraysAreEqual(lhs.pages, rhs.pages)
        );
    }

    if (lhs.type === 'link' && rhs.type === 'link') {
        return targetAreEqual(lhs.target, rhs.target);
    }

    if (lhs.type === 'computed' && rhs.type === 'computed') {
        return computedAreEqual(lhs.computed, rhs.computed);
    }

    return false;
}

function coverAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    return (
        refAreEqual(lhs.ref, rhs.ref) &&
        refAreEqual(lhs.refDark, rhs.refDark) &&
        Object.keys(PAGE_COVER_VALUE_KEYS).every((key) => lhs[key] === rhs[key])
    );
}

function refAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    if (lhs.kind !== rhs.kind) return false;

    // Compare all properties
    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (lhs[key] !== rhs[key]) return false;
    }

    return true;
}

function layoutAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    // If it's a string (preset), compare directly
    if (typeof lhs === 'string' && typeof rhs === 'string') {
        return lhs === rhs;
    }

    // If it's an object (options), compare properties
    if (typeof lhs === 'object' && typeof rhs === 'object') {
        return Object.keys(PAGE_LAYOUT_OPTION_KEYS).every((key) => lhs[key] === rhs[key]);
    }

    return false;
}

function computedSourceAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    return sourceAreEqual(lhs.source, rhs.source) && lhs.seed === rhs.seed;
}

function sourceAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    if (lhs.type !== rhs.type) return false;

    // Compare props
    if (!propsAreEqual(lhs.props, rhs.props)) return false;

    // Compare dependencies
    if (!dependenciesAreEqual(lhs.dependencies, rhs.dependencies)) return false;

    return true;
}

function propsAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (!(key in rhs)) return false;

        const lhsVal = lhs[key];
        const rhsVal = rhs[key];

        // Deep comparison for objects and arrays
        if (lhsVal !== rhsVal) {
            if (
                typeof lhsVal === 'object' &&
                typeof rhsVal === 'object' &&
                lhsVal !== null &&
                rhsVal !== null
            ) {
                if (Array.isArray(lhsVal) && Array.isArray(rhsVal)) {
                    if (lhsVal.length !== rhsVal.length) return false;
                    for (let i = 0; i < lhsVal.length; i++) {
                        if (!deepEqual(lhsVal[i], rhsVal[i])) return false;
                    }
                } else if (!Array.isArray(lhsVal) && !Array.isArray(rhsVal)) {
                    if (!propsAreEqual(lhsVal, rhsVal)) return false;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        }
    }

    return true;
}

function deepEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;

    if (typeof lhs !== typeof rhs) return false;

    if (typeof lhs === 'object' && lhs !== null && rhs !== null) {
        if (Array.isArray(lhs) && Array.isArray(rhs)) {
            if (lhs.length !== rhs.length) return false;
            for (let i = 0; i < lhs.length; i++) {
                if (!deepEqual(lhs[i], rhs[i])) return false;
            }
            return true;
        } else if (!Array.isArray(lhs) && !Array.isArray(rhs)) {
            const lhsKeys = Object.keys(lhs);
            const rhsKeys = Object.keys(rhs);
            if (lhsKeys.length !== rhsKeys.length) return false;
            for (const key of lhsKeys) {
                if (!(key in rhs)) return false;
                if (!deepEqual(lhs[key], rhs[key])) return false;
            }
            return true;
        }
    }

    return false;
}

function dependenciesAreEqual(lhs: any, rhs: any): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (!(key in rhs)) return false;

        const lhsDep = lhs[key];
        const rhsDep = rhs[key];

        // Use deep comparison for ref and value as they might be complex objects
        if (!deepEqual(lhsDep.ref, rhsDep.ref) || !deepEqual(lhsDep.value, rhsDep.value)) {
            return false;
        }
    }

    return true;
}

function targetAreEqual(lhs: any, rhs: any): boolean {
    return refAreEqual(lhs, rhs);
}

function computedAreEqual(lhs: any, rhs: any): boolean {
    return sourceAreEqual(lhs, rhs);
}

function reusableContentsAreEqual(
    lhs: { [key: string]: RevisionReusableContent },
    rhs: { [key: string]: RevisionReusableContent }
): boolean {
    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (!(key in rhs)) return false;

        const lhsContent = lhs[key];
        const rhsContent = rhs[key];

        if (
            lhsContent.id !== rhsContent.id ||
            lhsContent.title !== rhsContent.title ||
            lhsContent.document !== rhsContent.document ||
            !editsAreEqual(lhsContent.edits, rhsContent.edits) ||
            !gitFileStateAreEqual(lhsContent.git, rhsContent.git) ||
            !computedSourceAreEqual(lhsContent.computed, rhsContent.computed)
        ) {
            return false;
        }
    }

    return true;
}

function variablesAreEqual(
    lhs: RevisionVariables | undefined,
    rhs: RevisionVariables | undefined
): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (lhs[key] !== rhs[key]) return false;
    }

    return true;
}

function tagsAreEqual(lhs: RevisionTags | undefined, rhs: RevisionTags | undefined): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    const lhsKeys = Object.keys(lhs);
    const rhsKeys = Object.keys(rhs);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (!(key in rhs)) return false;

        const lhsTag = lhs[key];
        const rhsTag = rhs[key];

        if (
            lhsTag.slug !== rhsTag.slug ||
            lhsTag.label !== rhsTag.label ||
            lhsTag.color !== rhsTag.color ||
            lhsTag.emoji !== rhsTag.emoji ||
            lhsTag.icon !== rhsTag.icon ||
            !editsAreEqual(lhsTag.edits, rhsTag.edits)
        ) {
            return false;
        }
    }

    return true;
}

function conflictsAreEqual(
    lhs: RevisionConflicts | undefined,
    rhs: RevisionConflicts | undefined
): boolean {
    if (lhs === rhs) return true;
    if (!lhs && !rhs) return true;
    if (!lhs || !rhs) return false;

    const lhsKeys = Object.keys(lhs.pages);
    const rhsKeys = Object.keys(rhs.pages);

    if (lhsKeys.length !== rhsKeys.length) return false;

    for (const key of lhsKeys) {
        if (lhs.pages[key] !== rhs.pages[key]) return false;
    }

    return true;
}
